import * as React from 'react';
import { observer } from 'mobx-react';
import { ConfirmingType, PageConfirmGiftViewModel } from './page-confirm-gift-view-model';
import { Formatter } from '../../../Shared/utils/formatter';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { CheckboxField } from '../../components/form-fields';
import { Form } from '../../primitives/form';
import { EditLink } from '../../primitives/link/link';
import { ErrorMessage, WebGivingErrorType } from '../../components/error-message/error-message';
import { AmountDisplay } from '../../components/amount-display/amount-display';
import { InfoMessage } from '../../components/info-message/info-message'
import { PaymentMethodSelector } from '../../components/payment-method-selector/payment-method-selector';
import { WebGivingPaymentResultStatus, PaymentMethodType, Currency } from '../../webgiving-generated';
import { FadeTransition } from '../../components/fade-transition';
import { PanelContentTransition } from '../../components/panel-content-transition';
import { GiftDescription } from '../../components/gift/gift-description';
import { GiveButton } from '../../components/gift/give-button';
import { BackButton } from '../../components/back-button/back-button';
import { getPageClasses } from '../helpers/get-page-classes';
import { PanelHeading } from '../../components/panel-heading';
import { getUniqueElementId } from '../../../Shared/utils/form-control-utils';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { PanelFooter } from '../../components/panel-footer';
import { pageFooter, pageFooterWithBackButton } from '../../main/webgiving-main.less';
import { TermsAndConditionsBody } from '../../components/terms-and-conditions';
import { Text } from '../../components/text';

import * as styles from './page-confirm.less';
import { CustomButton, ButtonStyle } from '../../primitives/button/button';
import { SvgIcon } from '../../components/svg-icon';
import AppleIcon from '../../assets/svg/apple-pay.svg';
import { SummaryTable } from '../../components/gift/summary-table';
import { GooglePayButton } from '../../components/google-pay-button';

export { PageConfirmGiftViewModel };

interface IPageConfirmGiftProps {
	vm: PageConfirmGiftViewModel;
	transitionState: string;
}

const errorMessageElementId = 'page-confirm-gift-error-message_' + getUniqueElementId();

@injectWebGivingProps
@observer
export class PageConfirmGift extends React.Component<IPageConfirmGiftProps & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;

	render() {
const { vm, transitionState } = this.props;
		const {
			recurringInfo,
			fundName,
			isLoading,
			pageShouldBeVisible,
			paymentMethod,
			error,
			paymentBlocked,
			responseTimeNotice,
			handlePaymentMethodChange,
			showTermsAndConditions,
			oneTimeGiftDateLabel,
			hideACH,
			googlePayClient,
			skippedGivePage,
			paymentDetailsText,
			confirmingType,
			termsAndConditionsUrl,
			privacyStatementUrl,
		} = vm;
		const elementsShouldEnter = transitionState === 'entered';
		const amountFormatted = Formatter.formatNumberForDisplay(vm.calculatedAmountWithFees, 2);
		const {
			PaymentLabel: paymentLabel,
			TradingName: tradingName,
			LegalName: legalName,
			Currency: currency,
			HasCustomTermsOrPrivacy: hasCustomLinks,
		} = vm.merchantInfo;
		const paymentMethodIsACH = paymentMethod.PaymentMethodType === PaymentMethodType.ACH;
		const shouldHideACH = hideACH;

		let payerCoversFeesCheckbox;
		if (vm.merchantSupportsPayerCoversFees) {
			payerCoversFeesCheckbox = (
				<div className={styles.payerCoversFeesCheckbox}>
					<CheckboxField
						formField={vm.payerElectedToCoverFees}
						label={this.payerCoverFeesLabel()}
						acceptanceTestTargetId="PayerCoversFeesCheckbox"
						disabled={responseTimeNotice}
						onChange={this.handleChangePayerCoversFees}
					/>
				</div>
			);
		}

		const splitLineItems = { gifts: vm.individuallyCalculatedAmountWithFees };
		const hasSplitItems = splitLineItems.gifts.length > 0;

		const shouldRenderApplePayIcon: boolean = !paymentBlocked && vm.applePayAvailable;
		const hasGooglePay: boolean = !paymentBlocked && Boolean(googlePayClient);
		const hasAlternatePaymentMethods: boolean = shouldRenderApplePayIcon || hasGooglePay;

		const termsAndConditionsPanel = () =>
			showTermsAndConditions
				? <TermsAndConditionsBody legalName={legalName || tradingName} paymentLabel={paymentLabel} hasCustomLinks={hasCustomLinks} termsAndConditionsUrl={termsAndConditionsUrl} privacyStatementUrl={privacyStatementUrl}/>
				: null;

		let numberOfGifts = null;
		if (hasSplitItems) {
			numberOfGifts = splitLineItems.gifts.length;
		}

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading, responseTimeNotice)} data-pp-at-target={pageShouldBeVisible ? `Confirm Gift page` : null}>
				<Form onSubmit={this.handleSubmit}>
					<PanelHeading vm={vm} className={styles.heading}>
						<FadeTransition in={elementsShouldEnter}>
							<div>
								<h1 className="panel-title"><Text>Confirm Your {paymentLabel.NounSentenceCase}</Text></h1>
								<AmountDisplay amount={vm.calculatedAmountWithFees} currencyCode={Currency[currency]} />
								<GiftDescription
									recurringInfo={recurringInfo}
									fundName={hasSplitItems ? `funds` : fundName}
									numberOfGifts={numberOfGifts}
									currencyCode={Currency[currency]}
									amountFormatted={amountFormatted}
									paymentLabel={paymentLabel}
									tradingName={tradingName}
									oneTimeGiftDateLabel={oneTimeGiftDateLabel}
									paymentDetailsText={paymentDetailsText}
								/>
								{hasSplitItems && <SummaryTable gifts={splitLineItems.gifts} totalAmount={vm.calculatedAmountWithFees} />}
								{recurringInfo && this.anniversaryReminder()}
								{payerCoversFeesCheckbox}
								{!skippedGivePage && <p className={styles.editLink}><EditLink branded={false} onClick={this.handleEditLinkClick} acceptanceTestTargetId="change-details"><Text>Change details</Text></EditLink></p>}
							</div>
						</FadeTransition>
					</PanelHeading>
					<div className="panel-body" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage error={error} id={errorMessageElementId} />
								{responseTimeNotice && <InfoMessage paymentLabel={paymentLabel} />}
								<h3 className={styles.paymentMethodHeading}><Text>Payment method</Text></h3>
								<PaymentMethodSelector
									paymentMethods={shouldHideACH ? vm.paymentMethods.filter(c=> c.PaymentMethodType !== PaymentMethodType.ACH) : vm.paymentMethods}
									field={vm.paymentMethodId}
									formControlId="payment-method-selector"
									onAddPaymentMethodClick={this.handleAddNewPaymentMethod}
									failedMethodId={this.getFailedPaymentMethodId()}
									paymentMethodErrorElementId={errorMessageElementId}
									onValueChange={handlePaymentMethodChange}
									cardPreference={vm.merchantInfo.CardPreference}
									merchantSupportsCard={vm.merchantSupportsCard}
									supportsAch={shouldHideACH ? false :vm.merchantSupportsACH}
								/>
								{!shouldHideACH && <FadeTransition key={paymentMethod.Id} in={paymentMethodIsACH}>
									<div>
										<AuthorizeDebitAccount vm={vm} />
									</div>
								</FadeTransition>}
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={pageFooter}>
						{termsAndConditionsPanel()}
						<div className={pageFooterWithBackButton}>
							<GiveButton
								frequency={recurringInfo && recurringInfo.frequency}
								amountFormatted={amountFormatted}
								currencyCode={Currency[currency]}
								paymentLabel={paymentLabel}
								isLoading={
									vm.applePayAvailable
										? isLoading && confirmingType === ConfirmingType.GIVING
										: isLoading
								}
								isBlocked={paymentBlocked}
								acceptanceTestTargetId="Next"
							/>
							{NewFeatures.WebGivingGooglePayUX 
								? hasAlternatePaymentMethods && (
										<div className={styles.giveButtonContainer}>
											<div className={styles.separator}>
												<span className={styles.separatorText}>
													<Text>Or</Text>
												</span>
											</div>
											{shouldRenderApplePayIcon && (
												<CustomButton
													acceptanceTestTargetId={'applePayConfirmButton'}
													isLoading={isLoading && confirmingType === ConfirmingType.APPLEPAY}
													buttonStyle={ButtonStyle.Custom}
													isBlocked={isLoading || paymentBlocked}
													className={styles.applePayButton}
													onClick={this.handleApplePaySubmit}
												>
													<SvgIcon svg={AppleIcon} className={styles.applePayIcon} />
												</CustomButton>
											)}
											{hasGooglePay && (
												<GooglePayButton
													client={googlePayClient}
													onClick={this.handleGooglePay}
												/>
											)}
										</div>
								  )
								: shouldRenderApplePayIcon && (
										<div className={styles.giveButtonContainer}>
											<div className={styles.separator}>
												<span className={styles.separatorText}>
													<Text>Or</Text>
												</span>
											</div>
											<CustomButton
												acceptanceTestTargetId={'applePayConfirmButton'}
												isLoading={isLoading && confirmingType === ConfirmingType.APPLEPAY}
												buttonStyle={ButtonStyle.Custom}
												isBlocked={isLoading || paymentBlocked}
												className={styles.applePayButton}
												onClick={this.handleApplePaySubmit}
											>
												<SvgIcon svg={AppleIcon} className={styles.applePayIcon} />
											</CustomButton>
										</div>
								  )}
							{!skippedGivePage && (
								<BackButton acceptanceTestTargetId="Back" onClick={this.handleToPreviousPage}>
									<Text>Back</Text>
								</BackButton>
							)}
						</div>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	private anniversaryReminder = () => {
		const {
			payerEnabledAnniversaryReminder,
			merchantInfo: { PaymentLabel },
		} = this.props.vm;

		return (
			<div className={styles.anniversaryReminder}>
				<CheckboxField
					formField={payerEnabledAnniversaryReminder}
					label={`Send me a reminder to review my ${PaymentLabel.NounLowerCase} each year`}
					acceptanceTestTargetId="anniversaryReminderCheckbox"
					ariaDescribedBy="anniversary-reminder-checkbox"
				/>
			</div>
		);
	};

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		if (this.props.vm.applePayAvailable) {
			this.props.vm.setConfirmingType(ConfirmingType.GIVING);
		}
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}

	private handleApplePaySubmit = () => {
		this.props.vm.setConfirmingType(ConfirmingType.APPLEPAY);
		raiseAction(new WebGivingUserAction.MakeApplePay(this.props.vm.calculatedAmountWithFees), this.props);
	}

	private handleGooglePay = () => {
		raiseAction(new WebGivingUserAction.MakeGooglePay(this.props.vm.calculatedAmountWithFees), this.props);
	}

	private handleAddNewPaymentMethod = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();

		raiseAction(new WebGivingUserAction.NewPaymentMethod(), this.props);
	}

	private handleEditLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();

		raiseAction(new WebGivingUserAction.ChangePaymentDetails(), this.props);
	}

	private handleChangePayerCoversFees = (payerCoversFees: boolean) => {
		raiseAction(new WebGivingUserAction.ChangePayerCoversFees(payerCoversFees), this.props);
	}

	private handleToPreviousPage = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.PreviousPage(), this.props);
	}

	private payerCoverFeesLabel = () => `Add ${this.props.vm.merchantInfo.PercentageOfPaymentAddedToCoverFees}% to contribute to processing costs*`;

	private getFailedPaymentMethodId() {
		const { error } = this.props.vm;
		if (error &&
			error.errorType === WebGivingErrorType.PaymentError && error.failedPaymentMethodId &&
			(error.errorReason === WebGivingPaymentResultStatus.CardFail || error.errorReason === WebGivingPaymentResultStatus.PaymentMethodNotSupported)) {
			return error.failedPaymentMethodId;
		}

		return undefined;
	}
}

@observer
class AuthorizeDebitAccount extends React.Component<{ vm: PageConfirmGiftViewModel }, {}> {
	render() {
		const { paymentMethod, authorizeDebitAccount, recurringInfo, merchantInfo: { TradingName: tradingName, PaymentLabel: paymentLabel } } = this.props.vm;
		const labelParams = {
			tradingName,
			nickname: paymentMethod.Nickname,
			reference: paymentMethod.DisplayReference,
		};

		let authorizeDebitLabel = recurringInfo
			? `I authorize ${tradingName} to debit ${paymentMethod.Nickname} (${paymentMethod.DisplayReference}) according to the amount and schedule above and the terms below.`
			: `I authorize ${tradingName} to debit the amount shown above from my account ${paymentMethod.Nickname} (${paymentMethod.DisplayReference}).`;

		authorizeDebitLabel = recurringInfo
			? `I authorize {{tradingName}} to debit {{nickname}} ({{reference}}) according to the amount and schedule above and the terms below.`
			: `I authorize {{tradingName}} to debit the amount shown above from my account {{nickname}} ({{reference}}).`;

		return (
			<div>
				<p>
					<CheckboxField formField={authorizeDebitAccount}
						translationParams={labelParams}
						label={authorizeDebitLabel}
						acceptanceTestTargetId="AuthorizeDebitAccountCheckbox" />
				</p>
				{recurringInfo &&
					<p className={styles.smallPrint}>
						{
							<Text params={{tradingName}}>
							{`If the above noted ${paymentLabel.NounLowerCase} date falls on a weekend or holiday, I understand that the ${paymentLabel.NounLowerCase} may be executed on the next business day. I understand that this authorization will remain in effect until I cancel it and that any changes in my account information or termination of this authorization must be made from within my Pushpay.com account or Pushpay mobile App at least 2 business days prior to the next ${paymentLabel.NounLowerCase} date. I acknowledge that the origination of ACH transactions to my account must comply with the provisions of U.S. law. I will not dispute {{tradingName}}'s recurring ${paymentLabel.NounLowerCase} with my bank so long as the transactions corresponds to the terms indicated in this agreement.`}
							</Text>
						}
					</p>
				}
			</div>
		);
	}
}
